import type { CheckInStatus } from '@empower-safety/shared-types';
import { CheckCircle2, Clock, FlaskConical, Phone } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  type CheckInDto,
  useCheckInsList,
  useCompleteCheckIn,
  useCreateCheckIn,
  useForceCancelCheckIn,
} from '../api/useCheckIns.js';
import { type RosterMember, useRoster } from '../api/useRoster.js';
import { CheckInStatusPill } from '../components/CheckInStatusPill.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';
import { formatDateTime, formatRelative } from '../lib/format.js';

type Filter = 'active' | 'overdue' | 'completed' | 'cancelled';

const tabs: { key: Filter; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function CheckIns() {
  const [filter, setFilter] = useState<Filter>('active');
  const query = useCheckInsList({ status: filter as CheckInStatus, limit: 100 });
  // Overdue is the check-in that matters — a worker past their safety deadline.
  // Pull its count regardless of the current tab so the Overdue tab can shout it.
  const overdueQuery = useCheckInsList({ status: 'overdue', limit: 100 });
  const overdueCount = overdueQuery.data?.check_ins.length ?? 0;
  const rows = query.data?.check_ins ?? [];

  // Roster join for the worker's phone — a check-in about to lapse is exactly
  // when you want to call them.
  const roster = useRoster();
  const memberById = useMemo(() => {
    const map = new Map<string, RosterMember>();
    for (const m of roster.data?.members ?? []) map.set(m.user_id, m);
    return map;
  }, [roster.data]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex items-end justify-between">
        <div>
          <span className="eyebrow">Operations</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Check-ins
          </h1>
          <p className="text-sm text-ink-faint">
            Timed safety check-ins across your organisation. Overdue entries raise an alarm
            automatically.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TestCheckInButton />
          <div className="inline-flex gap-0.5 rounded-md border border-line bg-surface-1 p-1 shadow-xs">
            {tabs.map((t) => {
              const isActive = filter === t.key;
              const flagOverdue = t.key === 'overdue' && overdueCount > 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setFilter(t.key)}
                  className={cn(
                    'rounded-sm px-3.5 py-1.5 text-xs font-semibold transition',
                    isActive
                      ? flagOverdue
                        ? 'bg-danger text-white'
                        : 'bg-brand-purple text-white'
                      : flagOverdue
                        ? 'text-danger'
                        : 'text-ink-faint hover:text-ink',
                  )}
                >
                  {t.label}
                  {flagOverdue && (
                    <span
                      className={cn(
                        'ml-2 rounded-full px-1.5 py-px font-mono text-[10px] font-bold',
                        isActive ? 'bg-white/20 text-white' : 'bg-danger-soft text-danger',
                      )}
                    >
                      {overdueCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {query.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load check-ins: {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && rows.length === 0 && (
        <EmptyState
          icon={filter === 'overdue' ? Clock : CheckCircle2}
          title={
            filter === 'active'
              ? 'No active check-ins'
              : filter === 'overdue'
                ? 'No overdue check-ins'
                : `No ${filter} check-ins`
          }
          description={
            filter === 'active'
              ? 'Workers start check-ins from the mobile app. Use the dev button to create a test one that fires overdue in 1 min.'
              : 'Change the filter above, or trigger a test check-in with a short duration to see how overdue works.'
          }
        />
      )}

      {rows.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Status</Th>
                <Th>Worker</Th>
                <Th>Contact</Th>
                <Th>Scheduled</Th>
                <Th>Duration</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {rows.map((r) => (
                <CheckInRow key={r.id} row={r} member={memberById.get(r.user_id) ?? null} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CheckInRow({ row, member }: { row: CheckInDto; member: RosterMember | null }) {
  const complete = useCompleteCheckIn();
  const forceCancel = useForceCancelCheckIn();
  // Live ticking countdown for active entries. Resets every second; cheap.
  const [, tick] = useState(0);
  useEffect(() => {
    if (row.status !== 'active') return;
    const timer = window.setInterval(() => tick((n) => n + 1), 1_000);
    return () => clearInterval(timer);
  }, [row.status]);

  const scheduledMs = new Date(row.scheduled_for).getTime();
  const remainingMs = scheduledMs - Date.now();
  const overdue = row.status === 'overdue' || (row.status === 'active' && remainingMs < 0);
  // Active and within 5 min of the deadline — surface it before it lapses.
  const imminent = row.status === 'active' && remainingMs >= 0 && remainingMs < 5 * 60_000;

  return (
    <tr
      className={cn('hover:bg-surface-3', overdue ? 'bg-red-50/40' : imminent && 'bg-amber-50/40')}
    >
      <Td>
        <CheckInStatusPill status={row.status} />
      </Td>
      <Td>
        {row.user_display_name ? (
          <div className="text-sm text-ink">{row.user_display_name}</div>
        ) : (
          <div
            className="font-mono text-xs text-ink-faint"
            title="Worker name unavailable — showing user id"
          >
            {`${row.user_id.slice(0, 8)}…`}
          </div>
        )}
      </Td>
      <Td>
        {member?.phone_e164 ? (
          <a
            href={`tel:${member.phone_e164}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100"
            title={`Call ${member.display_name}`}
          >
            <Phone className="h-3.5 w-3.5" />
            {member.phone_e164}
          </a>
        ) : (
          <span className="text-sm text-ink-mute">No phone</span>
        )}
      </Td>
      <Td>
        <div
          className={cn(
            'text-sm font-medium',
            overdue ? 'text-danger' : imminent ? 'text-warning' : 'text-ink',
          )}
        >
          {overdue
            ? `overdue ${formatOverdue(Date.now() - scheduledMs)}`
            : row.status === 'active'
              ? formatRemaining(remainingMs)
              : formatRelative(row.scheduled_for)}
        </div>
        <div className="text-xs text-ink-mute">{formatDateTime(row.scheduled_for)}</div>
      </Td>
      <Td className="text-sm text-ink-faint">{row.duration_minutes} min</Td>
      <Td className="text-right">
        {row.status === 'active' && (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => complete.mutate(row.id)}
              disabled={complete.isPending}
              className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
            >
              I'm OK
            </button>
            <button
              type="button"
              onClick={() => forceCancel.mutate(row.id)}
              disabled={forceCancel.isPending}
              className="rounded-md border border-line bg-white px-3 py-1 text-xs font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </Td>
    </tr>
  );
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'overdue';
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  if (minutes > 0) return `in ${minutes}m ${seconds}s`;
  return `in ${seconds}s`;
}

// How long a check-in has been past its deadline — "3m" / "1h 12m".
function formatOverdue(ms: number): string {
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 60) return `${Math.max(1, totalMin)}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Dev-only: create a 1-minute check-in so you can watch it flip to 'overdue' and raise an
// overdue_check_in alarm. Tree-shaken in production.
function TestCheckInButton() {
  const createCheckIn = useCreateCheckIn();
  if (!import.meta.env.DEV) return null;
  return (
    <button
      type="button"
      onClick={() => createCheckIn.mutate(1)}
      disabled={createCheckIn.isPending}
      className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-blue-400 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800 transition hover:bg-blue-100 disabled:opacity-50"
      title="Create a 1-minute check-in so you can watch it go overdue"
    >
      <FlaskConical className="h-3.5 w-3.5" />
      {createCheckIn.isPending ? 'Creating…' : 'Test 1-min check-in'}
    </button>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
